import React, { useEffect, useRef } from 'react';

import { Box } from '@chakra-ui/react';

import { GraphScale } from 'components/Visualization/PixiGraph/common/GraphScale';
import { useGraphDnd } from 'components/Visualization/PixiGraph/hooks/useGraphDnd';
import { useGraphMenuTooltip } from 'components/Visualization/PixiGraph/hooks/useMenuTooltip';
import { usePostureMap } from 'components/Visualization/PixiGraph/maps/PostureMap/hooks/usePostureMap';

import { ActivityGraphMenu } from './ActivityGraphMenu';

const ACTIVITY_GRAPH_HORIZONTAL_GAP = 280;
const ACTIVITY_GRAPH_VERTICAL_GAP = 120;

export interface ActivityGraphCanvasProps {
  nodeCount: number;
  showVulnerability: boolean;
  toggleVulnerability: () => void;
}

export const ActivityGraphCanvas = ({
  nodeCount,
  showVulnerability,
  toggleVulnerability,
}: ActivityGraphCanvasProps) => {
  const graphRef = useRef<HTMLDivElement>(null);
  const { app, graph, renderer } = usePostureMap({
    ref: graphRef,
    horizontalGap: ACTIVITY_GRAPH_HORIZONTAL_GAP,
    verticalGap: ACTIVITY_GRAPH_VERTICAL_GAP,
    alignSourceNodesVertically: true,
    backgroundColor: 0xffffff,
  });
  const graphMenu = useGraphMenuTooltip({
    app,
    graph,
    renderer,
    ref: graphRef,
  });
  const { listeners } = useGraphDnd({ ref: graphRef, app });
  useEffect(() => {
    const graphContainer = graphRef.current;
    if (!graphContainer) return;

    let animationFrame: number;
    const fitAndCenterGraph = () => {
      const width = graphContainer.offsetWidth;
      const height = graphContainer.offsetHeight;
      if (!width || !height || !graph.nodes.length) return;

      app.renderer.resize(width, height);

      const minX = Math.min(...graph.nodes.map(node => node.x));
      const maxX = Math.max(...graph.nodes.map(node => node.x + node.w));
      const minY = Math.min(...graph.nodes.map(node => node.y));
      const maxY = Math.max(...graph.nodes.map(node => node.y + node.h));
      const graphWidth = Math.max(maxX - minX, 1);
      const graphHeight = Math.max(maxY - minY, 1);
      const scale = 1;

      app.stage.scale.set(scale);
      app.stage.x = (width - graphWidth * scale) / 2 - minX * scale;
      app.stage.y = (height - graphHeight * scale) / 2 - minY * scale;
    };
    const scheduleGraphLayout = () => {
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(fitAndCenterGraph);
    };
    const resizeObserver = new ResizeObserver(scheduleGraphLayout);

    resizeObserver.observe(graphContainer);
    scheduleGraphLayout();

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
    };
  }, [app, graph, nodeCount]);

  return (
    <Box
      h="full"
      w="full"
      pos="absolute"
      onContextMenu={event => event.preventDefault()}
    >
      <Box
        ref={graphRef}
        className="graph-canvas-v1"
        h="full"
        w="full"
        {...listeners}
        {...graphMenu.listeners}
      />
      <ActivityGraphMenu app={app} graph={graph} renderer={renderer} />
      <GraphScale
        app={app}
        graph={graph}
        showVulnerability={showVulnerability}
        toggleVulnerability={toggleVulnerability}
      />
    </Box>
  );
};
